import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ArrowLeft, Calendar, MapPin, ChevronDown, ChevronUp,
  CheckCircle2, Clock, AlertCircle, FileText, Phone,
} from "lucide-react";
import { usePortalAuth, portalApi } from "../../context/PortalAuthContext";
import PortalShell from "./PortalShell";
import SEO from "../../components/SEO";

const STATUS = {
  outstanding: { label: "Awaiting schedule", color: "#FF9500", Icon: AlertCircle },
  scheduled: { label: "Scheduled", color: "#0A84FF", Icon: Clock },
  in_progress: { label: "In progress", color: "#FFCC00", Icon: Loader2 },
  completed: { label: "Completed", color: "#22c55e", Icon: CheckCircle2 },
  invoiced: { label: "Invoiced", color: "#94a3b8", Icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "#737373", Icon: AlertCircle },
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n);
}

export default function PortalJobDetail() {
  const { id } = useParams();
  const { customer, loading } = usePortalAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    if (!customer) return;
    portalApi.get(`/portal/jobs/${id}`)
      .then((r) => setJob(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Couldn't load this job."));
  }, [customer, id]);

  if (loading) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" />
        </div>
      </PortalShell>
    );
  }
  if (!customer) return <Navigate to="/portal/login" replace />;

  const meta = job ? STATUS[job.status] || STATUS.scheduled : null;
  const StatusIcon = meta?.Icon;
  const hasBreakdown = Array.isArray(job?.cost_breakdown) && job.cost_breakdown.length > 0;

  return (
    <PortalShell>
      <SEO
        title="Job details · Tech Emergency portal"
        description="Service job details."
        path={`/portal/jobs/${id}`}
        noIndex
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          type="button"
          onClick={() => navigate("/portal")}
          className="te-overline inline-flex items-center gap-2 hover:text-white mb-6 text-zinc-400"
          data-testid="portal-job-back"
        >
          <ArrowLeft size={14} /> All jobs
        </button>

        {!job && !error && (
          <div className="te-card p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div className="te-card p-6 border border-red-500/30 bg-red-500/10 text-red-300 text-sm flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {job && (
          <article data-testid={`portal-job-detail-${job.id}`}>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] font-bold uppercase"
                style={{ background: `${meta.color}1f`, color: meta.color }}
              >
                <StatusIcon size={9} />
                {meta.label}
              </div>
              <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                <Calendar size={11} /> {fmtDate(job.completed_at || job.created_at)}
              </span>
              {job.suburb && (
                <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <MapPin size={11} /> {job.suburb}
                </span>
              )}
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-8">
              {job.service}
            </h1>

            {/* Cost */}
            <div className="te-card p-6 mb-6" data-testid="portal-job-cost-card">
              <div className="flex items-baseline justify-between gap-4 mb-1">
                <div className="te-overline text-[10px]">/ Total</div>
                <div className="text-3xl font-extrabold tracking-tighter" style={{ color: "var(--te-primary)" }}>
                  {fmtMoney(job.cost_total)}
                </div>
              </div>
              {hasBreakdown && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowBreakdown((v) => !v)}
                    className="mt-2 text-xs text-zinc-400 hover:text-white transition inline-flex items-center gap-1.5"
                    data-testid="portal-job-toggle-breakdown"
                  >
                    {showBreakdown ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {showBreakdown ? "Hide breakdown" : "Show breakdown"}
                  </button>
                  {showBreakdown && (
                    <ul
                      className="mt-3 pt-3 border-t border-white/10 space-y-1.5"
                      data-testid="portal-job-breakdown"
                    >
                      {job.cost_breakdown.map((li, i) => (
                        <li key={i} className="flex items-center justify-between text-sm">
                          <span className="text-zinc-300">{li.label}</span>
                          <span className="font-medium tabular-nums">{fmtMoney(li.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Service notes */}
            {job.customer_notes && (
              <div className="te-card p-6 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={14} style={{ color: "var(--te-primary)" }} />
                  <div className="te-overline text-[10px]">/ Your service notes</div>
                </div>
                <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                  {job.customer_notes}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="te-card p-6 text-center">
              <div className="text-sm text-zinc-400 mb-3">
                Got a follow-up? We&apos;re happy to help — no callout fee.
              </div>
              <a
                href="tel:1800836390"
                className="te-btn te-btn-primary"
                data-testid="portal-job-call-cta"
              >
                <Phone size={14} /> Call 1800 836 390
              </a>
              <div className="mt-3">
                <Link
                  to="/book"
                  className="text-xs text-zinc-500 hover:text-white transition"
                >
                  …or book online
                </Link>
              </div>
            </div>
          </article>
        )}
      </div>
    </PortalShell>
  );
}
